import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import axios from '../../api/axios'
import { portalState } from '../../hooks/usePortal'
import useTranslation from '../../hooks/useTranslation'
import { Input, Label, ErrorMsg } from './styles'

const Background = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    padding: 3rem 1rem;
`

const Card = styled.section`
    background: ${({ theme }) => theme.area?.background || '#fff'};
    border: 1px solid ${({ theme }) => theme.border?.color || '#e5e7eb'};
    border-radius: 0.75rem;
    padding: 2rem;
    width: 100%;
    max-width: 440px;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const Title = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
`

const OrgName = styled.span`
    color: ${({ theme }) => theme.btn?.primary || '#2563eb'};
`

const Description = styled.p`
    margin: 0;
    font-size: 0.95rem;
    color: ${({ theme }) => theme.font?.muted || '#6b7280'};
    line-height: 1.5;
`

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${({ theme }) => theme.border?.color || '#e5e7eb'};
    margin: 0;
`

const Button = styled.button`
    padding: 0.65rem 1rem;
    background: ${({ theme }) => theme.button?.primary?.background || '#2563eb'};
    color: ${({ theme }) => theme.button?.primary?.color || '#fff'};
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
    cursor: pointer;

    &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const AcceptInvitation = () => {
    const { token } = useParams()
    const navigate = useNavigate()
    const snap = useSnapshot(portalState)
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)

    const orgName = snap.name || ''

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!name || !password) return
        setLoading(true)
        setError(null)
        try {
            await axios.post('/portal/acceptInvitation', { token, name, password })
            navigate('/loggedout', { state: { message: 'portal.invitation.accepted' } })
        } catch (err) {
            setError(err.response?.data?.error?.name || 'error.unknown')
            setLoading(false)
        }
    }

    return (
        <Background>
            <Card>
                <Header>
                    <Title>{t('portal.invitation.title')}</Title>
                    <Description>
                        {t('portal.invitation.orgLine').replace('{org}', '')}
                        <OrgName>{orgName}</OrgName>
                        {t('portal.invitation.orgLineSuffix')}
                    </Description>
                    <Description>{t('portal.invitation.instructions')}</Description>
                </Header>

                <Divider />

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Label>
                        {t('portal.invitation.nameLabel')}
                        <Input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoFocus
                            placeholder={t('portal.invitation.namePlaceholder')}
                        />
                    </Label>
                    <Label>
                        {t('portal.invitation.passwordLabel')}
                        <Input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder={t('portal.invitation.passwordPlaceholder')}
                        />
                    </Label>
                    {error && <ErrorMsg>{error}</ErrorMsg>}
                    <Button type="submit" disabled={loading || !name || !password}>
                        {loading ? t('portal.invitation.submitting') : t('portal.invitation.submit')}
                    </Button>
                </form>
            </Card>
        </Background>
    )
}

export default AcceptInvitation
